/**
 * React Hook for AI Usage Management
 * Provides easy access to usage limits, tracking, and upgrade functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { AIUsageService, AIUsageLimits, UserPlan, AIFeatureType } from '../lib/aiUsageService';

export interface UseAIUsageReturn {
  // Usage checking
  checkLimit: (featureType: AIFeatureType) => Promise<AIUsageLimits>;
  canUseFeature: (featureType: AIFeatureType) => Promise<boolean>;
  
  // Usage tracking
  incrementUsage: (
    featureType: AIFeatureType,
    metadata?: {
      requestType?: string;
      apiProvider?: string;
      modelUsed?: string;
      tokensUsed?: number;
      processingTimeMs?: number;
      success?: boolean;
      errorMessage?: string;
    }
  ) => Promise<boolean>;
  
  // Plan management
  userPlan: UserPlan | null;
  loadUserPlan: () => Promise<void>;
  upgradePlan: (planType: 'free' | 'pro' | 'premium', billingCycle?: 'monthly' | 'yearly') => Promise<boolean>;
  
  // UI helpers
  formatLimits: (limits: AIUsageLimits) => string;
  getUpgradeRecommendation: (limits: AIUsageLimits | null) => {
    shouldUpgrade: boolean;
    message: string;
    recommendedPlan: 'pro' | 'premium';
  };
  
  // Loading states
  loading: boolean;
  error: string | null;
}

export const useAIUsage = (): UseAIUsageReturn => {
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get current user ID
   */
  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }, []);

  /**
   * Check if user can use a specific AI feature
   */
  const checkLimit = useCallback(async (featureType: AIFeatureType): Promise<AIUsageLimits> => {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    return AIUsageService.checkFeatureLimit(userId, featureType);
  }, [getCurrentUserId]);

  /**
   * Simple boolean check for feature availability
   */
  const canUseFeature = useCallback(async (featureType: AIFeatureType): Promise<boolean> => {
    try {
      const limits = await checkLimit(featureType);
      return limits.can_use;
    } catch (error) {
      console.error('Error checking feature availability:', error);
      return false;
    }
  }, [checkLimit]);

  /**
   * Increment usage counter for a feature
   */
  const incrementUsage = useCallback(async (
    featureType: AIFeatureType,
    metadata?: {
      requestType?: string;
      apiProvider?: string;
      modelUsed?: string;
      tokensUsed?: number;
      processingTimeMs?: number;
      success?: boolean;
      errorMessage?: string;
    }
  ): Promise<boolean> => {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.error('Cannot increment usage: User not authenticated');
      return false;
    }

    return AIUsageService.incrementUsage(
      userId,
      featureType,
      metadata?.requestType,
      metadata?.apiProvider,
      metadata?.modelUsed,
      metadata?.tokensUsed,
      metadata?.processingTimeMs,
      metadata?.success ?? true,
      metadata?.errorMessage
    );
  }, [getCurrentUserId]);

  /**
   * Load user's current plan
   */
  const loadUserPlan = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('User not authenticated');
        return;
      }

      const plan = await AIUsageService.getUserPlan(userId);
      setUserPlan(plan);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user plan';
      console.error('Error loading user plan:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getCurrentUserId]);

  /**
   * Upgrade user to a new plan
   */
  const upgradePlan = useCallback(async (
    planType: 'free' | 'pro' | 'premium',
    billingCycle: 'monthly' | 'yearly' = 'monthly'
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('User not authenticated');
        return false;
      }

      const result = await AIUsageService.upgradePlan(userId, planType, billingCycle);
      
      if (result.success) {
        // Reload user plan to reflect changes
        await loadUserPlan();
        return true;
      } else {
        setError(result.error || 'Failed to upgrade plan');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upgrade plan';
      console.error('Error upgrading plan:', err);
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getCurrentUserId, loadUserPlan]);

  /**
   * Format usage limits for display
   */
  const formatLimits = useCallback((limits: AIUsageLimits): string => {
    return AIUsageService.formatUsageLimits(limits);
  }, []);

  /**
   * Get upgrade recommendation based on usage
   */
  const getUpgradeRecommendation = useCallback((limits: AIUsageLimits | null) => {
    return AIUsageService.getUpgradeRecommendation(limits);
  }, []);

  // Load user plan on mount with small delay to avoid race condition
  useEffect(() => {
    // Small delay to let app initialization complete first
    const timer = setTimeout(() => {
      loadUserPlan();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [loadUserPlan]);

  return {
    // Usage checking
    checkLimit,
    canUseFeature,
    
    // Usage tracking
    incrementUsage,
    
    // Plan management
    userPlan,
    loadUserPlan,
    upgradePlan,
    
    // UI helpers
    formatLimits,
    getUpgradeRecommendation,
    
    // Loading states
    loading,
    error
  };
};

/**
 * Higher-order function to create usage-aware AI feature wrappers
 */
export const withUsageTracking = <T extends any[], R>(
  featureType: AIFeatureType,
  originalFunction: (...args: T) => Promise<R>,
  options: {
    requestType?: string;
    getApiProvider?: (...args: T) => string;
    getModelUsed?: (...args: T) => string;
    getTokensUsed?: (result: R) => number;
  } = {}
) => {
  return async (...args: T): Promise<R> => {
    const startTime = Date.now();
    
    // Get user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    // Check usage limits before proceeding
    const limits = await AIUsageService.checkFeatureLimit(user.id, featureType);
    if (!limits.can_use) {
      throw new Error(`Feature limit exceeded: ${limits.reason}`);
    }

    let result: R | undefined;
    let success = true;
    let errorMessage: string | undefined;

    try {
      // Execute the original function
      result = await originalFunction(...args);
    } catch (error) {
      success = false;
      errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw error;
    } finally {
      // Track usage regardless of success/failure
      const processingTime = Date.now() - startTime;
      
      await AIUsageService.incrementUsage(
        user.id,
        featureType,
        options.requestType,
        options.getApiProvider?.(...args),
        options.getModelUsed?.(...args),
        success && result ? options.getTokensUsed?.(result) : undefined,
        processingTime,
        success,
        errorMessage
      );
    }

    if (result === undefined) {
      throw new Error('Function execution failed');
    }

    return result;
  };
};

/**
 * React hook for creating usage-limited AI functions
 */
export const useUsageLimitedFunction = <T extends any[], R>(
  featureType: AIFeatureType,
  originalFunction: (...args: T) => Promise<R>,
  options?: {
    requestType?: string;
    getApiProvider?: (...args: T) => string;
    getModelUsed?: (...args: T) => string;
    getTokensUsed?: (result: R) => number;
  }
) => {
  const { checkLimit, incrementUsage } = useAIUsage();

  return useCallback(async (...args: T): Promise<R> => {
    const startTime = Date.now();
    
    // Check limits first
    const limits = await checkLimit(featureType);
    if (!limits.can_use) {
      throw new Error(`Feature limit exceeded: ${limits.reason}`);
    }

    let result: R | undefined;
    let success = true;
    let errorMessage: string | undefined;

    try {
      result = await originalFunction(...args);
    } catch (error) {
      success = false;
      errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw error;
    } finally {
      // Track usage
      const processingTime = Date.now() - startTime;
      
      await incrementUsage(featureType, {
        requestType: options?.requestType,
        apiProvider: options?.getApiProvider?.(...args),
        modelUsed: options?.getModelUsed?.(...args),
        tokensUsed: success && result ? options?.getTokensUsed?.(result) : undefined,
        processingTimeMs: processingTime,
        success,
        errorMessage
      });
    }

    if (result === undefined) {
      throw new Error('Function execution failed');
    }

    return result;
  }, [checkLimit, incrementUsage, originalFunction, featureType, options]);
};
